'use client';

import { type FC, type PropsWithChildren, useMemo } from 'react';
import dynamic from 'next/dynamic';

import { ChakraProvider } from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { SWRConfig } from 'swr';
import { SWRDevTools } from 'swr-devtools';

import { AuthProvider } from '@features/auth';

import { localStorageProvider } from '@entities/marketplace_scan';

import { AnalyticsProvider, yandexProvider } from '@shared/analytics';

import theme from '../theme';

const AppProgressProvider = dynamic(
  () => import('@bprogress/next').then((mod) => mod.AppProgressProvider),
  { ssr: false },
);

const Providers: FC<PropsWithChildren> = ({ children }) => {
  const emotionCache = useMemo(
    () =>
      createCache({
        key: 'my',
        speedy: false,
      }),
    [],
  );

  return (
    <AuthProvider>
      <AnalyticsProvider providers={[yandexProvider]}>
        <SWRDevTools>
          <SWRConfig
            value={{
              // do not create the same request in 5 secs
              dedupingInterval: 5000,
              errorRetryCount: 0,
              provider: localStorageProvider,
            }}
          >
            <CacheProvider value={emotionCache}>
              <ChakraProvider theme={theme}>
                <AppProgressProvider
                  height="4px"
                  color="#17479E"
                  options={{ showSpinner: false }}
                  shallowRouting
                >
                  {children}
                </AppProgressProvider>
              </ChakraProvider>
            </CacheProvider>
          </SWRConfig>
        </SWRDevTools>
      </AnalyticsProvider>
    </AuthProvider>
  );
};

export default Providers;
